import { OpenSans } from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import { gsap } from "gsap";
import { Container, Text } from "pixi.js";

import { AbstractReel } from "../../../abstracts/elements/AbstractReel";
import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { AbstractView } from "../../../abstracts/modules/AbstractView";
import { AbstractWinScreen } from "../../../abstracts/modules/screens/AbstractWinScreen";
import { LevelName, ScatterStates, TimelineLabels, WinType } from "../../../abstracts/types/enums";
import {
    IBetWayWinData,
    ITotalWinDecoratorData,
    IWinLevel,
    waysWinAnimationData,
} from "../../../abstracts/types/interfaces";
import { fitDisplayObjectToWidth } from "../../../abstracts/utils/GraphicUtils";
import { getTotalFromArrayByPropertyName } from "../../../abstracts/utils/JSUtils";
import { playSound } from "../../../abstracts/utils/SoundUtils";
import { DDD } from "../../DDD";
import { DDDSoundNames, DDDWaysTTF } from "../../assetsLoader/DDDAssetsNames";
import {
    getWinLevel,
    symbolConfig,
    xbet3BigWinsThreeshold,
    xbet3Name,
} from "../../config/DDDConfig";
import DDDReel from "../../modules/Reels/DDDReel";
import { DDDScatterSymbol } from "../../modules/Reels/symbols/DDDScatterSymbol";
import { DDDSymbol } from "../../modules/Reels/symbols/DDDSymbol";
import { DDDWildSymbol } from "../../modules/Reels/symbols/DDDWildSymbol";
import { DDDXNudgeSymbol } from "../../modules/Reels/symbols/DDDXNudgeSymbol";
import { DDDSpecialSymbolNames } from "../../types/DDDEnums";

export const totalWinHandler = (data: ITotalWinDecoratorData): void => {
    const {
        winLevels = [],
        winAmount = DDD.model.gameData.brokeTheBank
            ? DDD.model.gameData.accumulatedRoundWin
            : DDD.model.gameData.win,
        timeline = gsap.timeline(),
        winScreen = new AbstractWinScreen(`WinScreen`, new AbstractView()),
        betValue = DDD.model.gameData.playedBetValue,
        finishTextAnimationDuration = 0.7,
        finishSpineAnimationDuration = 5,
        winIsBelowStake = false,
        reels = [],
        winSymbolsPos = DDD.model.winningPos,
        betWayWins = DDD.model.gameData.betWayWins,
        onDecoratorComplete = () => {},
        onDecoratorStart = () => {},
    } = data;

    if (winAmount === 0) {
        onDecoratorComplete();

        return;
    }

    timeline.addLabel(TimelineLabels.TOTAL_WIN_DECORATOR_START);
    onDecoratorStart();

    // animate symbols start
    timeline.addLabel(
        TimelineLabels.START_SYMBOLS_WIN_ANIMATION,
        TimelineLabels.TOTAL_WIN_DECORATOR_START
    );

    const winMultiplier: number = winAmount / betValue;
    let minBigWinIndex: number = winLevels.findIndex(
        (level: IWinLevel) => level.name === LevelName.BIG_WIN_1
    );
    const winLevel: IWinLevel = getWinLevel(winMultiplier);

    let winType: WinType = winLevel.winType;

    const waysWon: number = getTotalFromArrayByPropertyName(betWayWins, `numberOfCombinations`);

    const symbolsAndWaysTimeline = gsap.timeline({
        smoothChildTiming: true,
        autoRemoveChildren: true,
        onStart: () => {
            if (winIsBelowStake) {
                if (
                    DDD.model.data.mode === AbstractGameMode.FREESPIN ||
                    DDD.model.data.mode === AbstractGameMode.SUPER_FREESPIN
                ) {
                    playSound({ name: DDDSoundNames.WIN_SWEEP });
                }
            } else {
                playSound({ name: DDDSoundNames.WIN_SWEEP });
            }
        },
    });

    const symbolsWinTl = gsap.timeline({});

    let playedSoundOnce: boolean = false;

    reels.forEach((reel: AbstractReel) => {
        const visibleSymbols = (reel as DDDReel).getAllVisibleStopSymbols();

        if (winSymbolsPos.length > 0) {
            visibleSymbols.forEach((symbol: AbstractSymbol, symbolIndex) => {
                if (!winSymbolsPos[reel.reelId][symbolIndex]) {
                    if (
                        !(
                            DDD.model.winningPos[reel.reelId].includes(true) &&
                            DDD.model.xNudgeMap[reel.reelId]
                        )
                    ) {
                        symbolsWinTl.add((symbol as DDDSymbol).getNoWinAnimation(), 0);
                    }

                    if (
                        symbol instanceof DDDXNudgeSymbol &&
                        symbol.visible &&
                        DDD.model.winningPos[reel.reelId].includes(true) &&
                        DDD.model.xNudgeMap[reel.reelId]
                    ) {
                        symbolsAndWaysTimeline.add(symbol.getWinAnimation(), 0);
                    }
                } else {
                    if (!winIsBelowStake) {
                        if (symbol instanceof DDDScatterSymbol) {
                            symbolsAndWaysTimeline.add(
                                symbol.getWinAnimation(ScatterStates.WIN),
                                0.1
                            );
                        }
                        if (symbol instanceof DDDXNudgeSymbol) {
                            symbolsAndWaysTimeline.add(symbol.getWinAnimation(), 0);
                            if (!playedSoundOnce) {
                                symbolsAndWaysTimeline.call(
                                    () => {
                                        playSound({ name: DDDSoundNames.XNUDGE_WIN });
                                    },
                                    undefined,
                                    0
                                );
                                playedSoundOnce = true;
                            }
                        } else {
                            symbolsAndWaysTimeline.add(symbol.getWinAnimation(), 0);
                        }
                    }
                }
            });
        }
    });

    symbolsAndWaysTimeline.add(symbolsWinTl, 0);

    /////////////
    if (betWayWins.length >= 1) {
        const allWaysWinData: waysWinAnimationData[] = [];

        betWayWins.forEach((betWayData: IBetWayWinData) => {
            const wayWinSymbols: AbstractSymbol[] = [];

            reels.forEach((gameReel, reelIndex) => {
                const reel = gameReel as DDDReel;

                reel.getAllVisibleStopSymbols();
                const reelWinSymbols = reel.getWinningSymbols(winSymbolsPos[reelIndex]);

                reelWinSymbols.forEach((symbol) => {
                    const cond1 = reelIndex <= betWayData.length - 1;
                    const cond2 = symbol.name === betWayData.symbol;

                    const cond3 =
                        symbol.name === DDDSpecialSymbolNames.XN_1 ||
                        symbol.name === DDDSpecialSymbolNames.XN_2 ||
                        symbol.name === DDDSpecialSymbolNames.XN_3;
                    const cond4 = symbol.isWild;

                    if (
                        winSymbolsPos[reelIndex][symbol.positionInArray - 1] &&
                        cond1 &&
                        (cond2 || cond3 || cond4)
                    ) {
                        wayWinSymbols.push(symbol as DDDSymbol);
                    }
                });
            });

            if (wayWinSymbols.length > 0) {
                winScreen.show();

                wayWinSymbols.forEach((symbol, symbolIndex) => {
                    if (symbolIndex === wayWinSymbols.length - 1) {
                        const textContainer: Container = new Container();
                        textContainer.name = `waysContainer`;

                        let valueText = new Text(
                            `${(betWayData.amount * betWayData.multiplier).toFixed(2)}`,
                            {
                                fontFamily: DDDWaysTTF.FAMILY,
                                fontSize: `22px`,
                                dropShadow: true,
                                dropShadowAlpha: 0.8,
                                dropShadowBlur: 0.8,
                                dropShadowColor: 0x000000,
                                dropShadowDistance: 2,
                                fill: `#ffffff`,
                                letterSpacing: 1,
                                lineJoin: `round`,
                            }
                        );
                        valueText.name = `valueText`;
                        fitDisplayObjectToWidth(valueText, 118);

                        textContainer.addChild(valueText);

                        const waysWonString =
                            betWayData.numberOfCombinations === 1
                                ? DDD.apiPlugin.translations.translate(`Way`)
                                : DDD.apiPlugin.translations.translate(`Ways`);

                        let waysText = new Text(
                            `${betWayData.numberOfCombinations} ${waysWonString}`.toUpperCase(),
                            {
                                fontFamily: OpenSans.FAMILY,
                                // fontStyle: `italic`,
                                fontWeight: `bold`,
                                fontSize: `14px`,
                                dropShadow: true,
                                dropShadowAlpha: 0.8,
                                dropShadowBlur: 0.8,
                                dropShadowColor: 0x000000,
                                dropShadowDistance: 2,
                                fill: `#ffffff`,
                                letterSpacing: 1,
                                lineJoin: `round`,
                            }
                        );
                        waysText.name = `waysText`;
                        fitDisplayObjectToWidth(waysText, 118);
                        waysText.y = valueText.height - 4;

                        textContainer.addChild(waysText);

                        if (valueText.width >= waysText.width) {
                            waysText.x = valueText.width / 2 - waysText.width / 2;
                        } else {
                            valueText.x = waysText.width / 2 - valueText.width / 2;
                        }

                        textContainer.pivot.set(textContainer.width / 2, textContainer.height / 2);

                        const symbolGlobalPosition = symbol.getGlobalPosition();
                        const winScreenSymbolLocalPosition =
                            winScreen.toLocal(symbolGlobalPosition);
                        textContainer.x =
                            winScreenSymbolLocalPosition.x + symbolConfig.symbolWidth / 2;
                        textContainer.y =
                            winScreenSymbolLocalPosition.y + symbolConfig.symbolHeight / 2;

                        if (symbol instanceof DDDWildSymbol) {
                            if (symbol.size === 4) {
                                textContainer.x = 0;
                                textContainer.y = 44;
                            }
                        } else if (symbol instanceof DDDXNudgeSymbol) {
                            if (symbol.size === 4) {
                                textContainer.y = 46;
                            } else if (symbol.size === 3) {
                                textContainer.y = 108;
                            } else if (symbol.size === 2) {
                                textContainer.y = 170;
                            }
                        }

                        if (allWaysWinData.length === 0) {
                            allWaysWinData.push({
                                symbol: symbol as DDDSymbol,
                                textContainers: [textContainer],
                            });
                        } else {
                            let symbolAlreadyExists = false;
                            allWaysWinData.forEach((wayData) => {
                                if (wayData.symbol === symbol) {
                                    wayData.textContainers.push(textContainer);
                                    symbolAlreadyExists = true;
                                }
                            });

                            if (!symbolAlreadyExists) {
                                allWaysWinData.push({
                                    symbol: symbol as DDDSymbol,
                                    textContainers: [textContainer],
                                });
                            }
                        }

                        winScreen.addChild(textContainer);

                        const containerTl = gsap.timeline({
                            smoothChildTiming: true,
                            autoRemoveChildren: true,
                            onComplete: () => {
                                textContainer.destroy();
                            },
                        });

                        const showContainerAnimation = gsap.to(textContainer, {
                            alpha: 1,
                            duration: 0.2,
                        });
                        const moveAndContainerAnimation = gsap.to(textContainer, {
                            y: `-=${symbolConfig.symbolHeight}`,
                            duration: 1,
                        });
                        const hideContainerAnimation = gsap.to(textContainer, {
                            alpha: 0,
                            duration: 0.3,
                        });

                        containerTl.add(showContainerAnimation, 0);
                        containerTl.add(moveAndContainerAnimation, 0.4);
                        containerTl.add(hideContainerAnimation, `>-0.3`);

                        symbolsAndWaysTimeline.add(containerTl, 0);
                    }
                });
            }
        });

        allWaysWinData.forEach((wayData) => {
            if (wayData.textContainers.length > 1) {
                const symbol = wayData.symbol;
                const symbolLocalPosition = winScreen.toLocal(symbol.getGlobalPosition());

                const isXNudge = symbol instanceof DDDXNudgeSymbol;

                if (symbol.size === 1) {
                    const symbolHeightOffset = 1.24;
                    const distanceBetweenWins =
                        (symbolConfig.symbolHeight * symbolHeightOffset) /
                        (wayData.textContainers.length + 1);

                    wayData.textContainers.forEach((container, index) => {
                        const newYPos = distanceBetweenWins * (index + 1);

                        container.y =
                            newYPos -
                            (symbolConfig.symbolHeight * symbolHeightOffset -
                                symbolConfig.symbolHeight) /
                                2 +
                            symbolLocalPosition.y;
                    });
                }

                if (symbol.size === 4) {
                    // ! vezi si daca size = 3 la nudge
                    wayData.textContainers.forEach((container, index) => {
                        container.x = isXNudge ? container.x : 0;

                        let yOffset = -66;
                        if (wayData.textContainers.length === 3) {
                            yOffset = -108;
                        } else if (wayData.textContainers.length === 4) {
                            yOffset = -128;
                        }
                        container.y = yOffset + (468 / wayData.textContainers.length) * index;
                    });
                } else if (symbol.size === 3) {
                    wayData.textContainers.forEach((container, index) => {
                        let yOffset = 16;
                        if (wayData.textContainers.length === 3) {
                            yOffset = -20;
                        } else if (wayData.textContainers.length === 4) {
                            yOffset = -36;
                        }
                        container.y = yOffset + (384 / wayData.textContainers.length) * index;
                    });
                } else if (symbol.size === 2) {
                    wayData.textContainers.forEach((container, index) => {
                        let yOffset = 112;
                        if (wayData.textContainers.length === 3) {
                            yOffset = 88;
                        } else if (wayData.textContainers.length === 4) {
                            yOffset = 75;
                        }
                        container.y = yOffset + (244 / wayData.textContainers.length) * index;
                    });
                }
            }
        });
    }

    /////////////

    timeline.add(symbolsAndWaysTimeline, TimelineLabels.START_SYMBOLS_WIN_ANIMATION);
    let brokeTheBankDelay = DDD.model.gameData.brokeTheBank ? 0.2 : 0;
    let labelTime: number = symbolsAndWaysTimeline.totalDuration() / 2 + brokeTheBankDelay;

    timeline.addLabel(TimelineLabels.FINISH_SYMBOLS_WIN_ANIMATION, `>-=${labelTime}`);

    // end animate symbols

    const addSkip = () => {
        const skipToNextLevel = () => {
            const nextLabel: string = timeline.nextLabel();

            timeline.seek(timeline.labels[nextLabel], false);
        };

        data.flowStateController?.setSkipCallback(skipToNextLevel);
    };

    if (
        !winIsBelowStake ||
        (winIsBelowStake && winType === WinType.BIG) ||
        (winIsBelowStake && winType === WinType.MAX_CAP)
    ) {
        if (
            winMultiplier < winLevels[minBigWinIndex].min ||
            (DDD.model.buyFeatureName === xbet3Name && winMultiplier < xbet3BigWinsThreeshold)
        ) {
            switch (winType) {
                case WinType.SMALL:
                    winScreen.handleSmallWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });
                    break;

                case WinType.MEDIUM:
                    winScreen.handleMediumWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });
                    break;

                case WinType.LARGE:
                    winScreen.handleLargeWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });
                    break;

                default:
                    winScreen.handleTinyWins({
                        timeline: timeline,
                        level: winLevel,
                        animationDuration: winLevel.animationDuration,
                        callback: () => addSkip(),
                        winAmount: winAmount,
                        winMultiplier: winMultiplier,
                        winType: winType,
                        waysWon: waysWon,
                    });

                    break;
            }
        } else {
            winLevels.forEach((level: IWinLevel, index) => {
                if (index >= minBigWinIndex && winMultiplier >= level.min) {
                    timeline.addLabel(
                        level.name,
                        `${TimelineLabels.TOTAL_WIN_DECORATOR_START}+=${labelTime}`
                    );
                    labelTime += level.animationDuration;
                }
            });

            winLevels.forEach((level: IWinLevel, index) => {
                if (index >= minBigWinIndex && winMultiplier >= level.min) {
                    if (level.name === LevelName.MAX_CAP) {
                        winType = WinType.MAX_CAP;

                        winScreen.handleMaxCap({
                            timeline: timeline,
                            level: level,
                            animationDuration: labelTime,
                            callback: () => {
                                data.flowStateController?.setSkipCallback(undefined);
                            },
                            winAmount: winAmount,
                            winMultiplier: winMultiplier,
                            winType: WinType.MAX_CAP,
                            betAmount: betValue,
                            // waysWon: waysWon,
                        });
                    } else {
                        winScreen.handleBigWins({
                            timeline: timeline,
                            level: level,
                            finalLevel: winLevel,
                            animationDuration: labelTime,
                            callback: () => addSkip(),
                            winAmount: winAmount,
                            winMultiplier: winMultiplier,
                            winType: WinType.BIG,
                            betAmount: betValue,
                        });
                    }
                }
            });
        }

        switch (winType) {
            case WinType.BIG:
                timeline.addLabel(TimelineLabels.ON_BIG_WINS_COMPLETE_START);
                winScreen.onBigWinsComplete({
                    timeline: timeline,
                    winType: winType,
                    winAmount: winAmount,
                    level: winLevel,
                    waysWon: waysWon,
                    callback: () => addSkip(),
                });

                timeline.addLabel(
                    TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE,
                    TimelineLabels.ON_BIG_WINS_COMPLETE_FINISH
                );
                break;

            case WinType.MAX_CAP: {
                timeline.addLabel(TimelineLabels.ON_MAX_CAP_COMPLETE_START);

                winScreen.onMaxCapComplete({ timeline: timeline, callback: () => addSkip() });

                timeline.call(
                    () => {
                        data.flowStateController?.setSkipCallback(undefined);
                    },
                    undefined,
                    `${TimelineLabels.ON_MAX_CAP_COMPLETE_START}-=0.1`
                );

                timeline.addLabel(
                    TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE,
                    TimelineLabels.ON_MAX_CAP_COMPLETE_FINISH
                );

                break;
            }
            default:
                timeline.addLabel(TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE);
        }
    } else {
        timeline.addLabel(TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE);
    }

    timeline.call(
        () => {
            onDecoratorComplete();

            winScreen.finish({
                winType: winType,
                timeline: timeline,
                finishTextAnimationDuration: finishTextAnimationDuration,
                finishSpineAnimationDuration: finishSpineAnimationDuration,
                waysWon: waysWon,
            });
        },
        undefined,
        TimelineLabels.TOTAL_WIN_DECORATOR_COMPLETE
    );
};
